import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { processQueue, getPendingCount } from '../services/offlineQueue';

/**
 * Hook to track online/offline status and manage sync queue
 * @returns {{
 *   isOnline: boolean,
 *   isSyncing: boolean,
 *   pendingCount: number,
 *   syncNow: () => Promise<void>
 * }}
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Sync pending mutations
  const syncNow = useCallback(async () => {
    if (isSyncing || !navigator.onLine) return;

    const count = await getPendingCount();
    if (count === 0) {
      setPendingCount(0);
      return;
    }

    setIsSyncing(true);
    try {
      const result = await processQueue(supabase, (processed, total) => {
        setPendingCount(total - processed);
      });

      // Refresh pending count after sync
      const remaining = await getPendingCount();
      setPendingCount(remaining);

      if (result.failed > 0) {
        console.warn(`Sync: ${result.success} succeeded, ${result.failed} failed`);
      }
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing]);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-sync when coming back online
      syncNow();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check pending count on mount
    getPendingCount().then(setPendingCount);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncNow]);

  return { isOnline, isSyncing, pendingCount, syncNow };
}
